import { useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { ITEM_TYPES, GRADED_TYPES, typeMeta } from "./itemTypes";
import { X, Plus, Trash2 } from "lucide-react";

const emptyCard = () => ({ term: "", definition: "" });

// Add / edit dialog for a module item.
export default function ItemForm({ mode, moduleId, sectionId, item, materials, exams, projects, skillTags, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [type, setType] = useState(isEdit ? item.type : "read");
  const [title, setTitle] = useState(isEdit ? item.title : "");
  const meta = typeMeta(type);
  const defaultSource = isEdit
    ? (item.material_id ? "material" : item.exam_id ? "exam" : item.project_id ? "project" : "url")
    : (meta.sources[0] || "url");
  const [source, setSource] = useState(defaultSource);
  const [url, setUrl] = useState(isEdit ? item.url || "" : "");
  const [refId, setRefId] = useState(isEdit ? item.material_id || item.exam_id || item.project_id || "" : "");
  const [cards, setCards] = useState(isEdit && item.cards ? item.cards.map((c) => ({ ...c })) : [emptyCard()]);
  const [tags, setTags] = useState(isEdit ? item.tags || [] : []);
  const [counts, setCounts] = useState(isEdit ? item.counts_toward_grade !== false : true);
  const [points, setPoints] = useState(isEdit && item.points != null ? String(item.points) : "");
  const [dueDate, setDueDate] = useState(isEdit && item.due_date ? item.due_date.slice(0, 10) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const graded = GRADED_TYPES.includes(type);
  const sourceOptions = typeMeta(type).sources;
  const refList = source === "material" ? materials : source === "exam" ? exams : source === "project" ? projects : [];

  const changeType = (t) => {
    setType(t);
    setSource(typeMeta(t).sources[0] || "url");
    setRefId("");
  };

  const toggleTag = (t) =>
    setTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const setCard = (i, key, val) =>
    setCards((prev) => prev.map((c, idx) => (idx === i ? { ...c, [key]: val } : c)));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      if (isEdit) {
        const body = { title, tags };
        if (item.url && source === "url") body.url = url;
        if (type === "flashcards") body.cards = cards;
        if (graded) {
          body.counts_toward_grade = counts;
          if (points !== "") body.points = Number(points);
          if (dueDate) body.due_date = dueDate;
        }
        await api.patch(`/modules/${moduleId}/items/${item.id}`, body);
      } else {
        const body = { type, title, tags };
        if (type === "flashcards") body.cards = cards;
        else if (source === "url") body.url = url;
        else if (source === "material") body.material_id = refId;
        else if (source === "exam") body.exam_id = refId;
        else if (source === "project") body.project_id = refId;
        if (graded) body.counts_toward_grade = counts;
        const res = await api.post(`/modules/${moduleId}/sections/${sectionId}/items`, body);
        if (graded && (points !== "" || dueDate)) {
          const patch = {};
          if (points !== "") patch.points = Number(points);
          if (dueDate) patch.due_date = dueDate;
          await api.patch(`/modules/${moduleId}/items/${res.data.id}`, patch);
        }
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center px-4 overflow-y-auto py-8" onClick={onClose}>
      <form data-testid="item-form" onSubmit={submit} className="nb-card-static p-6 w-full max-w-lg animate-pop space-y-4 my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold">{isEdit ? "Edit item" : "Add item"}</h3>
          <button type="button" onClick={onClose} className="w-9 h-9 border-2 border-[var(--gold)] rounded-lg flex items-center justify-center hover:bg-[var(--yellow)]"><X className="w-4 h-4" /></button>
        </div>

        {!isEdit && (
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider mb-1 block">Type</label>
            <select data-testid="item-form-type" value={type} onChange={(e) => changeType(e.target.value)} className="nb-input">
              {ITEM_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        )}

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider mb-1 block">Title</label>
          <input data-testid="item-form-title" required value={title} onChange={(e) => setTitle(e.target.value)} className="nb-input" placeholder="e.g. Chapter 1 Reading" />
        </div>

        {type !== "flashcards" && !isEdit && (
          <>
            {sourceOptions.length > 1 && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider mb-1 block">Source</label>
                <select data-testid="item-form-source" value={source} onChange={(e) => { setSource(e.target.value); setRefId(""); }} className="nb-input">
                  {sourceOptions.map((s) => (
                    <option key={s} value={s}>{s === "url" ? "External link" : s === "material" ? "Uploaded material" : s === "exam" ? "Exam in this course" : "Project in this course"}</option>
                  ))}
                </select>
              </div>
            )}
            {source === "url" ? (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider mb-1 block">Link URL</label>
                <input data-testid="item-form-url" required value={url} onChange={(e) => setUrl(e.target.value)} className="nb-input" placeholder="https://..." />
              </div>
            ) : (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider mb-1 block">
                  {source === "material" ? "Material" : source === "exam" ? "Exam" : "Project"}
                </label>
                <select data-testid="item-form-ref" required value={refId} onChange={(e) => setRefId(e.target.value)} className="nb-input">
                  <option value="">Select…</option>
                  {refList.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
                </select>
              </div>
            )}
          </>
        )}

        {isEdit && item.url && (
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider mb-1 block">Link URL</label>
            <input data-testid="item-form-url" value={url} onChange={(e) => setUrl(e.target.value)} className="nb-input" placeholder="https://..." />
          </div>
        )}

        {type === "flashcards" && (
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider mb-1 block">Cards (term → definition)</label>
            <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
              {cards.map((c, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <input data-testid={`card-term-${i}`} required value={c.term} onChange={(e) => setCard(i, "term", e.target.value)} className="nb-input text-sm flex-1" placeholder="Term" />
                  <input data-testid={`card-def-${i}`} required value={c.definition} onChange={(e) => setCard(i, "definition", e.target.value)} className="nb-input text-sm flex-[1.4]" placeholder="Definition" />
                  <button type="button" onClick={() => setCards((p) => p.filter((_, idx) => idx !== i))} disabled={cards.length === 1} className="nb-btn nb-btn-white text-sm mt-0.5 disabled:opacity-40"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <button type="button" data-testid="add-card-btn" onClick={() => setCards((p) => [...p, emptyCard()])} className="mt-2 nb-btn nb-btn-white text-sm flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Add card
            </button>
          </div>
        )}

        {skillTags.length > 0 && (
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider mb-1 block">Skill tags</label>
            <div className="flex flex-wrap gap-2">
              {skillTags.map((t) => (
                <button type="button" key={t} data-testid={`item-form-tag-${t}`} onClick={() => toggleTag(t)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border-2 transition-colors ${tags.includes(t) ? "bg-[var(--gold)] text-[var(--navy-on-gold)] border-[var(--gold)]" : "border-[var(--gold)]/50 text-[var(--gold-soft)] hover:border-[var(--gold)]"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
        )}

        {graded && (
          <div className="rounded-lg border-2 border-[var(--gold)]/50 p-4 space-y-3">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input data-testid="item-form-counts" type="checkbox" checked={counts} onChange={(e) => setCounts(e.target.checked)} className="w-4 h-4 accent-[var(--gold)]" />
              <span className="text-sm font-semibold flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" /> Counts towards grade
              </span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider mb-1 block">Points</label>
                <input data-testid="item-form-points" type="number" min="1" step="any" value={points} onChange={(e) => setPoints(e.target.value)} className="nb-input" placeholder="100" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider mb-1 block">Due date</label>
                <input data-testid="item-form-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="nb-input" />
              </div>
            </div>
            <p className="text-[11px] text-[var(--muted-text)]">Points and due date are stored on the linked exam/project — the Gradebook always matches.</p>
          </div>
        )}

        {error && <p data-testid="item-form-error" className="text-sm text-red-600">{error}</p>}
        <button data-testid="item-form-submit" disabled={busy} className="nb-btn nb-btn-yellow w-full">{busy ? "Saving…" : isEdit ? "Save changes" : "Add item"}</button>
      </form>
    </div>
  );
}
